import React, { useEffect, useState } from "react";
import QMSLayout from "../components/QMSLayout";
import { Database, Beaker, Trash2, ShieldCheck, AlertTriangle, Loader2, CheckCircle2, History, Download, PieChart, Activity, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_COLOUR = {
  production: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-400", dot: "bg-emerald-400" },
  beta: { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-400", dot: "bg-amber-400" },
  test: { bg: "bg-sky-500/10", border: "border-sky-500/30", text: "text-sky-400", dot: "bg-sky-400" },
};

export default function DataEnvironmentPage() {
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [preview, setPreview] = useState(null);
  const [target, setTarget] = useState("test");
  const [busy, setBusy] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [health, setHealth] = useState(null);

  const loadConfig = async () => {
    const r = await fetch(`${API}/data-environment/config`, { credentials: "include" });
    if (r.ok) setConfig(await r.json());
  };
  const loadHistory = async () => {
    const r = await fetch(`${API}/data-environment/cleanup/history?limit=10`, { credentials: "include" });
    if (r.ok) setHistory((await r.json()).items || []);
  };
  const loadPreview = async (t = target) => {
    const r = await fetch(`${API}/data-environment/cleanup/preview?target=${t}`, { credentials: "include" });
    if (r.ok) setPreview(await r.json());
  };
  const loadCoverage = async () => {
    const r = await fetch(`${API}/client-notifications/compliance-coverage`, { credentials: "include" });
    if (r.ok) setCoverage(await r.json());
  };
  const loadHealth = async () => {
    const r = await fetch(`${API}/data-environment/health`, { credentials: "include" });
    if (r.ok) setHealth(await r.json());
  };

  useEffect(() => {
    loadConfig();
    loadHistory();
    loadPreview("test");
    loadCoverage();
    loadHealth();
  }, []);

  const toggleBeta = async () => {
    if (!config) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/data-environment/toggle-beta`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: !config.beta_mode }),
      });
      if (r.ok) await loadConfig();
    } finally {
      setBusy(false);
    }
  };

  const onTargetChange = (t) => {
    setTarget(t);
    loadPreview(t);
  };

  const executeCleanup = async () => {
    if (confirmText !== "DELETE") return;
    setBusy(true);
    setCleanupResult(null);
    try {
      const r = await fetch(`${API}/data-environment/cleanup`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirm: true, target }),
      });
      if (r.ok) {
        setCleanupResult(await r.json());
        setShowConfirm(false);
        setConfirmText("");
        await Promise.all([loadPreview(target), loadHistory()]);
      } else {
        const err = await r.json().catch(() => ({}));
        alert("Cleanup failed: " + (err.detail || r.status));
      }
    } finally {
      setBusy(false);
    }
  };

  if (!config) {
    return (
      <QMSLayout title="Data Environment">
        <div className="flex justify-center h-64 items-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00C8E0]" />
        </div>
      </QMSLayout>
    );
  }

  const currentTier = TIER_COLOUR[config.current_tag] || TIER_COLOUR.test;

  return (
    <QMSLayout title="Data Environment">
      <div className="space-y-6" data-testid="data-environment-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Database className="w-6 h-6 text-[#00C8E0]" />
          <div>
            <h1 className="text-xl font-bold text-white">Data Environment</h1>
            <p className="text-slate-400 text-xs">Tag, isolate, and clean up non-production records.</p>
          </div>
        </div>

        {/* Current Environment Card */}
        <div className={`rounded-[3px] p-5 border ${currentTier.bg} ${currentTier.border}`} data-testid="current-env-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className={`inline-flex w-2.5 h-2.5 rounded-full ${currentTier.dot}`} />
              <div>
                <div className={`text-sm font-bold uppercase tracking-wider ${currentTier.text}`} data-testid="current-env-tag">
                  Current tag: {config.current_tag}
                </div>
                <div className="text-[11px] text-slate-400 mt-1">
                  DATA_ENVIRONMENT = <span className="text-slate-200">{config.data_environment}</span> · Beta mode: <span className="text-slate-200">{config.beta_mode ? "ON" : "OFF"}</span>
                </div>
              </div>
            </div>
            <button
              onClick={toggleBeta}
              disabled={busy}
              className={`flex items-center gap-2 px-4 py-2 text-xs font-bold rounded-[3px] transition-colors disabled:opacity-50 ${config.beta_mode ? "bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30" : "bg-[#1A6BCC] text-white hover:bg-[#2178DD]"}`}
              data-testid="toggle-beta-btn"
            >
              <Beaker className="w-4 h-4" />
              {config.beta_mode ? "Disable Beta Mode" : "Enable Beta Mode"}
            </button>
          </div>
        </div>

        {/* Environment Data Health — post-incident (2026-04-19) staleness detector */}
        {health && (
          <div
            className={`rounded-[3px] p-5 border ${
              health.banner
                ? "bg-red-500/10 border-red-500/40"
                : "bg-[#132340] border-[#1E3A5F]"
            }`}
            data-testid="env-data-health-card"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Activity className={`w-4 h-4 ${health.banner ? "text-red-400" : "text-[#00C8E0]"}`} />
                <div className="text-sm font-bold text-white">Environment Data Health</div>
              </div>
              <button
                onClick={loadHealth}
                className="text-[10px] text-slate-400 hover:text-white underline underline-offset-2"
                data-testid="env-health-refresh-btn"
              >
                Refresh
              </button>
            </div>

            {health.banner && (
              <div
                className="flex items-center gap-2 text-xs font-bold text-red-300 bg-red-500/5 border border-red-500/30 rounded-[3px] px-3 py-2 mb-4"
                data-testid="env-health-banner"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                {health.banner}
                <span className="text-[10px] font-normal text-red-300/70 ml-2">
                  Drop `chapter_enrichments` and restart backend to force re-seed.
                </span>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-[#0A1628] border border-[#1E3A5F] rounded-[3px] p-3">
                <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Pod environment</div>
                <div className="text-sm font-bold text-white" data-testid="env-health-pod">
                  {health.environment.current_tag}
                </div>
                <div className="text-[10px] text-slate-500 mt-1">
                  base = {health.environment.data_environment}
                </div>
              </div>
              <div className="bg-[#0A1628] border border-[#1E3A5F] rounded-[3px] p-3">
                <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Chapters loaded</div>
                <div className="text-sm font-bold text-white" data-testid="env-health-chapters">
                  {health.chapter_enrichments.total_chapters} / 38
                </div>
                <div className="text-[10px] text-slate-500 mt-1">
                  {health.chapter_enrichments.per_book?.map(b => `${b.book_id.replace('BOOK-','')}:${b.chapters}`).join(' ')}
                </div>
              </div>
              <div className="bg-[#0A1628] border border-[#1E3A5F] rounded-[3px] p-3">
                <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Total words</div>
                <div
                  className={`text-sm font-bold ${health.chapter_enrichments.is_stale ? "text-red-400" : "text-emerald-400"}`}
                  data-testid="env-health-words"
                >
                  {health.chapter_enrichments.total_words.toLocaleString()}
                </div>
                <div className="text-[10px] text-slate-500 mt-1">
                  floor {health.chapter_enrichments.stale_word_floor.toLocaleString()}
                </div>
              </div>
              <div className="bg-[#0A1628] border border-[#1E3A5F] rounded-[3px] p-3">
                <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Last re-seed</div>
                <div className="text-sm font-bold text-white" data-testid="env-health-last-seed">
                  {health.chapter_enrichments.latest_ingest_at
                    ? new Date(health.chapter_enrichments.latest_ingest_at).toISOString().slice(0, 10)
                    : "—"}
                </div>
                <div className="text-[10px] text-slate-500 mt-1">
                  {health.chapter_enrichments.latest_ingest_at
                    ? new Date(health.chapter_enrichments.latest_ingest_at).toISOString().slice(11, 19) + " UTC"
                    : "never"}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div className="bg-[#0A1628] border border-[#1E3A5F] rounded-[3px] p-3" data-testid="env-health-last-bundle">
                <div className="flex items-center gap-2 text-[10px] text-slate-400 uppercase tracking-wider mb-2">
                  <BookOpen className="w-3 h-3" /> Last bundle (editorial score)
                </div>
                {health.last_bundle ? (
                  <>
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-mono text-slate-200">{health.last_bundle.bundle_version || "—"}</span>
                      <span className={`text-sm font-bold ${
                        (health.last_bundle.overall_score || 0) >= 91
                          ? "text-emerald-400"
                          : (health.last_bundle.overall_score || 0) >= 75
                          ? "text-amber-400"
                          : "text-red-400"
                      }`}>
                        {health.last_bundle.overall_score ?? "—"}/100
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">
                      {health.last_bundle.environment} · {health.last_bundle.timestamp?.slice(0, 19)}
                    </div>
                  </>
                ) : (
                  <span className="text-[11px] text-slate-500">No bundle generated yet.</span>
                )}
              </div>
              <div className="bg-[#0A1628] border border-[#1E3A5F] rounded-[3px] p-3" data-testid="env-health-last-submission">
                <div className="flex items-center gap-2 text-[10px] text-slate-400 uppercase tracking-wider mb-2">
                  <CheckCircle2 className="w-3 h-3" /> Last submission package
                </div>
                {health.last_submission ? (
                  <>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm font-mono text-slate-200">{health.last_submission.submission_id}</span>
                      <span className="text-[10px] text-slate-400">
                        {health.last_submission.book_id} · {health.last_submission.edition}
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1">
                      <span className={
                        (health.last_submission.word_count || 0) >= 70000
                          ? "text-emerald-400"
                          : "text-red-400"
                      }>
                        {(health.last_submission.word_count || 0).toLocaleString()} words
                      </span>
                      {" · "}
                      <span className="text-slate-300">{health.last_submission.page_count || "—"} pages</span>
                      {" · "}
                      <span className="text-slate-300">{health.last_submission.distribution_status}</span>
                    </div>
                  </>
                ) : (
                  <span className="text-[11px] text-slate-500">No submission package generated yet.</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Taggable / Protected */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-4" data-testid="taggable-collections">
            <div className="flex items-center gap-2 text-xs font-bold text-slate-200 mb-3">
              <Database className="w-3.5 h-3.5 text-[#00C8E0]" /> Taggable collections
            </div>
            <div className="flex flex-wrap gap-1.5">
              {config.taggable_collections.map(c => (
                <span key={c} className="text-[10px] bg-[#0A1628] border border-[#1E3A5F] text-slate-300 px-2 py-1 rounded-[2px]">{c}</span>
              ))}
            </div>
          </div>
          <div className="bg-[#132340] border border-emerald-500/20 rounded-[3px] p-4" data-testid="protected-collections">
            <div className="flex items-center gap-2 text-xs font-bold text-emerald-300 mb-3">
              <ShieldCheck className="w-3.5 h-3.5" /> Protected collections — never touched
            </div>
            <div className="flex flex-wrap gap-1.5">
              {config.protected_collections.map(c => (
                <span key={c} className="text-[10px] bg-emerald-500/5 border border-emerald-500/20 text-emerald-300 px-2 py-1 rounded-[2px]">{c}</span>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-3">Released documents in Document Control are never deleted.</p>
          </div>
        </div>

        {/* Compliance opt-in coverage widget */}
        {coverage && (
          <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5" data-testid="compliance-coverage-widget">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-bold text-white">
                <PieChart className="w-4 h-4 text-[#00C8E0]" /> Compliance opt-in coverage
              </div>
              {(() => {
                const ov = coverage.overall || {};
                const tierColour = ov.tier === "green" ? "text-emerald-400" : ov.tier === "amber" ? "text-amber-400" : "text-red-400";
                return (
                  <div className="flex items-baseline gap-2">
                    <span className={`text-xl font-bold ${tierColour}`} data-testid="coverage-overall-pct">{ov.opt_in_pct}%</span>
                    <span className="text-[10px] text-slate-400">{ov.opted_in} / {ov.total_clients} clients opted in</span>
                  </div>
                );
              })()}
            </div>
            <p className="text-[11px] text-slate-400 mb-3">
              Per-industry opt-in rate for compliance update emails. Green ≥75%, Amber 40–74%, Red &lt;40%.
              Use this to nudge under-subscribed segments.
            </p>
            {coverage.by_industry.length === 0 ? (
              <div className="text-[11px] text-slate-500">No client accounts with industry tags yet.</div>
            ) : (
              <div className="space-y-1.5" data-testid="coverage-rows">
                {coverage.by_industry.map((r) => {
                  const bar = r.tier === "green" ? "bg-emerald-400" : r.tier === "amber" ? "bg-amber-400" : "bg-red-400";
                  return (
                    <div key={r.industry} className="bg-[#0A1628] border border-[#1E3A5F]/50 rounded-[3px] p-2.5" data-testid={`coverage-row-${r.industry}`}>
                      <div className="flex items-center justify-between text-[11px] mb-1">
                        <span className="text-slate-200 font-medium">{r.industry}</span>
                        <span className="text-slate-300">
                          <span className="font-bold">{r.opt_in_pct}%</span>
                          <span className="text-slate-500 ml-2">{r.opted_in}/{r.total_clients}</span>
                        </span>
                      </div>
                      <div className="h-1.5 bg-[#1E3A5F] rounded-full overflow-hidden">
                        <div className={`h-full ${bar} rounded-full transition-all`} style={{ width: `${Math.max(2, r.opt_in_pct)}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Cleanup Tool */}
        <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5" data-testid="cleanup-panel">
          <div className="flex items-center gap-2 text-sm font-bold text-white mb-3">
            <Trash2 className="w-4 h-4 text-red-400" /> Cleanup tool
          </div>
          <p className="text-xs text-slate-400 mb-4">
            Deletes records whose <code className="text-[#00C8E0]">environment</code> field matches the selected target.
            Writes an audit record to Document Control. Requires typing <b className="text-red-300">DELETE</b> to confirm.
          </p>

          <div className="flex items-center gap-2 mb-4">
            {["test", "beta", "both"].map(t => (
              <button
                key={t}
                onClick={() => onTargetChange(t)}
                className={`px-3 py-1.5 text-xs rounded-[3px] border transition-colors ${target === t ? "bg-[#1A6BCC] text-white border-[#2178DD]" : "bg-[#0A1628] text-slate-300 border-[#1E3A5F] hover:border-cyan-400"}`}
                data-testid={`cleanup-target-${t}`}
              >
                {t}
              </button>
            ))}
          </div>

          {preview && (
            <div className="bg-[#0A1628] border border-[#1E3A5F]/50 rounded-[3px] p-3 mb-4" data-testid="cleanup-preview">
              <div className="text-xs text-slate-300 mb-2">
                Matching records: <span className="text-cyan-400 font-bold text-base" data-testid="cleanup-preview-count">{preview.total_records}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 text-[10px]">
                {Object.entries(preview.by_collection).map(([k, v]) => (
                  <div key={k} className={`flex justify-between px-2 py-1 rounded ${v > 0 ? "bg-amber-500/10 text-amber-200" : "bg-transparent text-slate-500"}`}>
                    <span>{k}</span><span className="font-bold">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!showConfirm ? (
            <button
              onClick={() => setShowConfirm(true)}
              disabled={!preview || preview.total_records === 0 || busy}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-300 border border-red-500/40 hover:bg-red-500/30 text-xs font-bold rounded-[3px] transition-colors disabled:opacity-40"
              data-testid="open-confirm-cleanup-btn"
            >
              <AlertTriangle className="w-4 h-4" />
              Prepare cleanup ({target})
            </button>
          ) : (
            <div className="bg-red-500/5 border border-red-500/30 rounded-[3px] p-3" data-testid="cleanup-confirm-box">
              <div className="text-xs text-red-300 font-bold mb-2">Confirm irreversible delete</div>
              <p className="text-[11px] text-slate-400 mb-2">
                Type <b className="text-red-300">DELETE</b> below, then click Execute to remove
                <b className="text-red-200"> {preview?.total_records || 0}</b> records tagged
                <b className="text-red-200"> {target}</b>.
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="w-full bg-[#0A1628] text-white text-xs border border-red-500/40 rounded-[3px] px-3 py-2 mb-2 focus:outline-none focus:border-red-400"
                placeholder="Type DELETE to confirm"
                data-testid="cleanup-confirm-input"
              />
              <div className="flex gap-2">
                <button
                  onClick={executeCleanup}
                  disabled={confirmText !== "DELETE" || busy}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white text-xs font-bold rounded-[3px] disabled:opacity-40 hover:bg-red-600"
                  data-testid="execute-cleanup-btn"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Execute cleanup
                </button>
                <button
                  onClick={() => { setShowConfirm(false); setConfirmText(""); }}
                  className="px-4 py-2 bg-[#0A1628] text-slate-300 text-xs rounded-[3px] border border-[#1E3A5F] hover:border-slate-400"
                  data-testid="cancel-cleanup-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {cleanupResult && (
            <div className="mt-4 bg-emerald-500/5 border border-emerald-500/30 rounded-[3px] p-3" data-testid="cleanup-success">
              <div className="flex items-center gap-2 text-xs font-bold text-emerald-300 mb-1">
                <CheckCircle2 className="w-4 h-4" /> Cleanup complete
              </div>
              <div className="text-[11px] text-slate-400">
                Deleted <b className="text-emerald-200">{cleanupResult.total_deleted}</b> records.
                Audit record: <span className="text-cyan-400">{cleanupResult.audit_record_id}</span>.
              </div>
            </div>
          )}
        </div>

        {/* History */}
        <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5" data-testid="cleanup-history">
          <div className="flex items-center gap-2 text-sm font-bold text-white mb-3">
            <History className="w-4 h-4 text-[#00C8E0]" /> Recent cleanup audits
          </div>
          {history.length === 0 ? (
            <div className="text-xs text-slate-500">No cleanup audit records yet.</div>
          ) : (
            <div className="space-y-2">
              {history.map(h => (
                <div key={h.doc_id} className="bg-[#0A1628] border border-[#1E3A5F]/50 rounded-[3px] p-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs text-white font-bold">{h.name}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {h.doc_id}
                      {h.created_at ? ` · ${new Date(h.created_at).toLocaleString()}` : ""}
                      {h.archive_record_count != null ? ` · ${h.archive_record_count} records archived` : ""}
                      {h.archive_size_bytes ? ` · ${(h.archive_size_bytes / 1024).toFixed(1)} KB` : ""}
                    </div>
                    {h.archive_expires_at && (
                      <div className="text-[9px] text-slate-500 mt-0.5">
                        Archive retention ends: {new Date(h.archive_expires_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  {h.archive_available ? (
                    <a
                      href={`${API}/data-environment/cleanup/archive/${h.doc_id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold rounded-[3px] bg-[#1A6BCC] text-white hover:bg-[#2178DD] transition-colors shrink-0"
                      data-testid={`download-archive-${h.doc_id}`}
                    >
                      <Download className="w-3.5 h-3.5" /> Download archive
                    </a>
                  ) : (
                    <span className="text-[10px] text-slate-500 shrink-0" data-testid={`archive-unavailable-${h.doc_id}`}>Archive expired</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </QMSLayout>
  );
}
